import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileText, Loader2, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { formatCLP } from "@/lib/format";
import { toast } from "sonner";

export default function DocumentUpload({ appId, docType, scope = "income", adultId, expenseId, documents, onChange, disabled }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const relevant = (documents || []).filter((d) =>
    d.scope === scope && (scope === "income" || scope === "other_income" ? d.adult_id === adultId : scope === "expense" ? d.expense_id === expenseId : true));

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let lastApp = null;
    let okCount = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      form.append("doc_type", docType);
      form.append("scope", scope);
      if (adultId) form.append("adult_id", adultId);
      if (expenseId) form.append("expense_id", expenseId);
      try {
        const { data } = await api.post(`/applications/${appId}/documents`, form);
        lastApp = data.application;
        okCount++;
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    if (okCount > 0) {
      const verb = scope === "income" ? "cargado y analizado" : "cargado";
      toast.success(okCount === 1 ? `Documento ${verb}.` : `${okCount} documentos ${verb}s.`);
      if (lastApp) onChange?.(lastApp);
      else {
        const { data } = await api.get(`/applications/${appId}`);
        onChange?.(data);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (docId) => {
    try {
      await api.delete(`/applications/${appId}/documents/${docId}`);
      const { data } = await api.get(`/applications/${appId}`);
      onChange?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      {!disabled && (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="w-full border-2 border-dashed border-border rounded-xl p-5 flex items-center justify-center gap-3 hover:border-primary hover:bg-secondary/40 transition-colors"
          data-testid={`upload-${scope}-${adultId || expenseId || "x"}`}>
          {uploading ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : <UploadCloud className="w-5 h-5 text-primary" />}
          <span className="text-sm font-semibold text-primary">{uploading ? "Analizando documentos…" : "Subir documentos (puedes elegir varios · PDF, JPG, PNG)"}</span>
        </button>
      )}
      <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFile} />
      <div className="mt-3 space-y-2">
        {relevant.map((d) => {
          const ex = d.extraction || {};
          return (
            <div key={d.id} className="bg-secondary/50 rounded-lg p-3 flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <FileText className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{d.original_filename}</p>
                  {scope === "income" && (
                    ex.detected_monthly != null ? (
                      <p className="text-xs text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Detectado: {formatCLP(ex.detected_monthly)}/mes
                      </p>
                    ) : (
                      <p className="text-xs text-amber-700 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> No se pudo leer automáticamente (revisión manual)
                      </p>
                    )
                  )}
                </div>
              </div>
              {!disabled && (
                <button type="button" onClick={() => remove(d.id)} className="text-destructive shrink-0" data-testid={`delete-doc-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
